import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { DARK_BLUE_BAND_CLASS, DARK_BLUE_BAND_ICON_TILE_CLASS } from '@/styles/darkBlueBand';
import { SOFT_BLUE_FLAT_CLASS, SOFT_BLUE_GRADIENT_CLASS } from '@/styles/softBlueSurface';

type HeaderTint = 'gradient' | 'flat' | 'dark';
type PanelSize = 'default' | 'compact';

interface HeaderTintStyle {
  band: string;
  iconTile: string;
}

const HEADER_TINT_STYLE: Record<HeaderTint, HeaderTintStyle> = {
  gradient: { band: SOFT_BLUE_GRADIENT_CLASS, iconTile: ACCENT_MARK_CLASS },
  flat: { band: SOFT_BLUE_FLAT_CLASS, iconTile: ACCENT_MARK_CLASS },
  dark: { band: DARK_BLUE_BAND_CLASS, iconTile: DARK_BLUE_BAND_ICON_TILE_CLASS },
};

interface PanelSizeStyle {
  panel: string;
  tintedHeader: string;
  iconTile: string;
  icon: string;
}

const PANEL_SIZE_STYLE: Record<PanelSize, PanelSizeStyle> = {
  default: {
    panel: 'gap-4 p-5',
    tintedHeader: '-mx-5 -mt-5 px-5 py-3.5',
    iconTile: 'size-8 rounded-lg',
    icon: 'size-4',
  },
  compact: {
    panel: 'gap-3 p-4',
    tintedHeader: '-mx-4 -mt-4 min-h-9 px-4 py-1',
    iconTile: 'size-6 rounded-md',
    icon: 'size-3.5',
  },
};

interface DetailPanelProps {
  title: string;
  titleAddon?: ReactNode | undefined;
  icon: LucideIcon;
  actions?: ReactNode | undefined;
  hasTintedHeader?: boolean | undefined;
  headerTint?: HeaderTint | undefined;
  size?: PanelSize | undefined;
  className?: string | undefined;
  children: ReactNode;
}

export function DetailPanel({
  title,
  titleAddon,
  icon: Icon,
  actions,
  hasTintedHeader = false,
  headerTint = 'gradient',
  size = 'default',
  className,
  children,
}: DetailPanelProps) {
  const sizeStyle = PANEL_SIZE_STYLE[size];
  const tintStyle = HEADER_TINT_STYLE[hasTintedHeader ? headerTint : 'gradient'];

  return (
    <section
      aria-label={title}
      className={cn(
        'flex flex-col rounded-2xl bg-card shadow-sm ring-1 ring-primary/10',
        sizeStyle.panel,
        className,
      )}
    >
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-3',
          hasTintedHeader && 'rounded-t-2xl border-b border-primary/10',
          hasTintedHeader && sizeStyle.tintedHeader,
          hasTintedHeader && tintStyle.band,
        )}
      >
        <div className="flex items-center gap-2.5">
          <span
            className={cn(
              'flex shrink-0 items-center justify-center',
              sizeStyle.iconTile,
              tintStyle.iconTile,
            )}
          >
            <Icon className={sizeStyle.icon} aria-hidden />
          </span>
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="text-sm font-semibold">{title}</h2>
            {titleAddon}
          </div>
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}
